import { inject } from '@angular/core';
import { ApiQuery } from '@kinet-open-network/client/core/api-client';
import {
  callApiMethod,
  setError,
  setLoaded,
  setLoading,
  withResourceState,
} from '@kinet-open-network/client/shared/data-access/state';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  Document,
  SendDocumentPayload,
  UploadDocumentPayload,
} from '../models/document.model';
import { DocumentsService } from '../services/documents.service';

export const DocumentsStore = signalStore(
  { protectedState: false },
  withResourceState<Document>(DocumentsService, { loadOnInit: false }),
  withState<{ parentType: string | null; parentId: string | null }>({
    parentType: null,
    parentId: null,
  }),
  withMethods((store, service = inject(DocumentsService)) => ({
    queryByParent: async (
      parentType: string,
      parentId: string,
      apiQuery?: ApiQuery,
    ) => {
      patchState(store, setLoaded(), { paginated: null, parentType, parentId });
      const query = {
        ...apiQuery,
        filters: [
          apiQuery?.filters,
          `(parentType eq "${parentType}" and parentId eq "${parentId}")`,
        ]
          .filter(Boolean)
          .join(' and '),
        sort: ['-createdAt'],
      };
      return callApiMethod(() => service.query(query), {
        next: (documents) => {
          patchState(store, { paginated: documents }, setLoaded());
        },
        error: (error) => patchState(store, setError(error)),
      });
    },
    signDocument: (id: string, signature: string) => {
      patchState(store, setLoading());

      return callApiMethod(() => service.signDocument(id, signature), {
        next: (doc) => patchState(store, { model: doc }, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    generate: (id: string) => {
      patchState(store, setLoading());

      return callApiMethod(() => service.generate(id), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    upload(data: UploadDocumentPayload) {
      patchState(store, setLoading());
      return callApiMethod(() => service.upload(data), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    send: (id: string, data: SendDocumentPayload) => {
      patchState(store, setLoading());
      return callApiMethod(() => service.sendDocument(id, data), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
  })),
);
